import { useCallback, useEffect, useRef, useState } from "react";

// Animated close: flip the drawer to logically-closed IMMEDIATELY (so every
// open/close path agrees on the state), but keep it mounted with a `.closing`
// class for one animation so the slide-out + scrim fade can play.
export function useRightSidebarDrawer() {
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [rightSidebarClosing, setRightSidebarClosing] = useState(false);
  const rightSidebarCloseTimer = useRef(null);
  // Mirrors showRightSidebar so close can no-op without churning callback
  // identity. Several callers (logo click, mobile nav button) blindly call
  // "close" as a reset — playing the exit animation then would flash a drawer
  // that was never open.
  const isOpenRef = useRef(false);

  const closeRightSidebar = useCallback(() => {
    if (!isOpenRef.current) return;
    isOpenRef.current = false;
    setShowRightSidebar(false);
    const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return; // no exit animation for reduced-motion users
    if (rightSidebarCloseTimer.current) clearTimeout(rightSidebarCloseTimer.current);
    setRightSidebarClosing(true);
    rightSidebarCloseTimer.current = setTimeout(() => {
      setRightSidebarClosing(false);
      rightSidebarCloseTimer.current = null;
    }, 330); // keep in sync with the close animation duration in _layout.scss
  }, []);

  // Opening cancels any in-flight close so a quick reopen is never yanked shut by
  // a pending timer (the cause of the open/close flicker).
  const openRightSidebar = useCallback(() => {
    if (rightSidebarCloseTimer.current) {
      clearTimeout(rightSidebarCloseTimer.current);
      rightSidebarCloseTimer.current = null;
    }
    setRightSidebarClosing(false);
    isOpenRef.current = true;
    setShowRightSidebar(true);
  }, []);

  // The header button calls setShowRightSidebar(boolean) — route it so opening
  // opens cleanly and closing animates, exactly like every other path.
  const toggleRightSidebar = useCallback(
    (next) => (next ? openRightSidebar() : closeRightSidebar()),
    [openRightSidebar, closeRightSidebar]
  );

  useEffect(() => () => clearTimeout(rightSidebarCloseTimer.current), []);

  return { showRightSidebar, rightSidebarClosing, openRightSidebar, closeRightSidebar, toggleRightSidebar };
}
